import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert,
    Button,
    Collapse,
    Input,
    Progress,
    Segmented,
    Select,
    Switch,
    Tooltip,
    TreeSelect,
    message,
} from 'antd';
import { AimOutlined, StopOutlined } from '@ant-design/icons';
import { Sparkles, BookOpen } from 'lucide-react';
import { useEditor, EditorContent } from '@tiptap/react';
import { useRightPanelContext } from '../RightPanelContext';
import { useEditorContext } from '../../../contexts/EditorContext';
import { REVISE_PRESETS } from '../../constants';
import { REWRITE_PHASE } from '../../../hooks/useRewriteTask';
import { convertBlocksToTiptapContent } from '../../../utils/blockConverter';
import { scrollEditorToPos } from '../../../utils/editorScroll';
import { extensions } from '../../../extensions';
import { ChipGroup } from '../shared/ChipGroup';
import { ResultCard } from '../shared/ResultCard';
import { PreviewActions } from '../shared/PreviewActions';
import styles from '../../../styles/index.module.scss';

const { TextArea } = Input;

const LEVEL_LABEL = { 1: '章', 2: '节', 3: '小节' };

/** 统计 tiptap JSON 文档的字数（不含空白） */
export const countDocChars = (node) => {
    if (!node) return 0;
    if (Array.isArray(node)) return node.reduce((sum, n) => sum + countDocChars(n), 0);
    if (node.type === 'text') return (node.text || '').replace(/\s+/g, '').length;
    return countDocChars(node.content);
};

/** 把快捷要求和补充要求拼成最终发给后端的 newPrompt */
export const buildInstruction = (presetKey, extraText) => {
    const preset = REVISE_PRESETS.find((p) => p.key === presetKey);
    const extra = (extraText || '').trim();
    if (preset && extra) return `${preset.text}\n补充要求：${extra}`;
    if (preset) return preset.text;
    return extra;
};

/**
 * 章节改稿面板
 * 选章节 → 写要求（快捷 + 自由文本）→ 提交任务 → 进度 → 新旧对比预览 → 应用 / 放弃
 */
const ChapterRewritePanel = () => {
    const {
        editor,
        uiState,
        commonState,
        chapterForm,
        chapterState,
        chapterTreeData,
        rewritableChapters,
        outline,
        rewriteTask,
        findChapterRange,
        findHeadingNodeByBlockId,
        scrollToChapter,
        startChapterRewrite,
        updateChapterRewriteState,
        finishChapterRewrite,
        copyToClipboard,
        scrollContainerRef,
    } = useRightPanelContext();
    const { reloadDocument } = useEditorContext();

    const previewEditor = useEditor({ extensions, content: null, editable: false });
    const originalEditor = useEditor({ extensions, content: null, editable: false });
    const [previewView, setPreviewView] = useState('new');
    // 发起任务时记录的目标章节，用于结果回填
    const targetRef = useRef(null);

    const chapter = commonState.currentChapterInfo;
    const chapterAttrs = chapter?.node?.attrs || null;
    const chapterTitle = chapter?.node?.textContent || '';
    const isProtected = !!chapterAttrs?.isProtected;
    const rewritable = !!chapter && !!chapterAttrs?.isLowestChapter && !isProtected && !!chapter.detailId;

    const { phase, progress, statusText, error, result, isBusy } = rewriteTask;
    const hasResult = phase === REWRITE_PHASE.READY || phase === REWRITE_PHASE.CONFIRMING;

    // 当前章节下可改稿的小节（选中的是大章时用来引导选择）
    const childChapters = useMemo(() => {
        if (!chapter || rewritable) return [];
        const index = outline.findIndex((item) => item.blockId === chapter.blockId);
        if (index < 0) return [];
        const level = outline[index].level;
        const children = [];
        for (let i = index + 1; i < outline.length; i += 1) {
            if (outline[i].level <= level) break;
            if (outline[i].rewritable) children.push(outline[i]);
        }
        return children;
    }, [chapter, outline, rewritable]);

    const chapterOptions = useMemo(
        () =>
            (childChapters.length > 0 ? childChapters : rewritableChapters).map((item) => ({
                value: item.blockId || item.id,
                label: item.text,
                level: item.level,
            })),
        [childChapters, rewritableChapters],
    );

    // ========== 遮罩同步 ==========

    useEffect(() => {
        if (phase === REWRITE_PHASE.SUBMITTING || phase === REWRITE_PHASE.RUNNING) {
            updateChapterRewriteState({ progress, status: statusText });
        } else {
            finishChapterRewrite();
        }
    }, [phase, progress, statusText, updateChapterRewriteState, finishChapterRewrite]);

    // ========== 结果就绪：准备新旧对比 ==========

    useEffect(() => {
        if (phase !== REWRITE_PHASE.READY || !result || !editor) return;
        if (chapterState.previewChapterContent) return; // 已处理

        const target = targetRef.current;
        const headingInfo = target ? findHeadingNodeByBlockId(editor, target.blockId) : null;
        if (!headingInfo) {
            message.error('找不到改稿的章节，请重新选择');
            rewriteTask.reset();
            return;
        }
        const range = findChapterRange(editor, {
            node: headingInfo.node,
            pos: headingInfo.pos,
            level: headingInfo.node.attrs.level,
        });
        const headingEnd = range.headingPos + range.headingNode.nodeSize;
        const originalContent = editor.state.doc.slice(headingEnd, range.to).content.toJSON();

        chapterState.previewChapterContent = convertBlocksToTiptapContent(result.blocks);
        chapterState.previewOriginalContent = originalContent;
        chapterState.previewChapterRange = { from: headingEnd, to: range.to };
        setPreviewView('new');
        uiState.toolsOpen = true;
        message.success('改稿完成，请预览后选择应用或放弃');
    }, [
        phase,
        result,
        editor,
        chapterState,
        findHeadingNodeByBlockId,
        findChapterRange,
        rewriteTask,
        uiState,
    ]);

    useEffect(() => {
        if (!previewEditor) return;
        previewEditor.commands.setContent(chapterState.previewChapterContent || null);
    }, [previewEditor, chapterState.previewChapterContent]);

    useEffect(() => {
        if (!originalEditor) return;
        const nodes = chapterState.previewOriginalContent;
        originalEditor.commands.setContent(
            nodes && nodes.length > 0 ? { type: 'doc', content: nodes } : null,
        );
    }, [originalEditor, chapterState.previewOriginalContent]);

    const clearPreview = useCallback(() => {
        chapterState.previewChapterContent = null;
        chapterState.previewOriginalContent = null;
        chapterState.previewChapterRange = null;
    }, [chapterState]);

    // ========== 选择章节 ==========

    const handlePickChapter = useCallback(
        (blockId) => {
            const item = outline.find((o) => (o.blockId || o.id) === blockId);
            if (!item || !editor) return;
            editor.commands.setNodeSelection(item.pos);
            scrollEditorToPos(editor, item.pos, {
                container: scrollContainerRef.current,
                offset: 32,
            });
        },
        [editor, outline, scrollContainerRef],
    );

    const handleLocate = useCallback(() => {
        if (!chapter || !editor) return;
        scrollToChapter(editor, chapter);
    }, [chapter, editor, scrollToChapter]);

    // ========== 发起改稿 ==========

    const handleStart = useCallback(async () => {
        if (!editor || !chapter) return;
        if (!rewritable) {
            message.warning('请选择一个可改稿的小节');
            return;
        }
        const instruction = buildInstruction(chapterForm.preset, chapterForm.instruction);
        if (!instruction) {
            message.warning('请选择一个快捷要求，或填写修改要求');
            return;
        }
        clearPreview();
        targetRef.current = {
            blockId: chapter.blockId,
            level: chapter.level,
            title: chapterTitle,
        };
        startChapterRewrite({
            blockId: chapter.blockId,
            pos: chapter.pos,
            detailId: chapter.detailId,
            level: chapter.level,
            node: chapter.node,
        });
        scrollToChapter(editor, chapter);
        await rewriteTask.start({
            detailId: chapter.detailId,
            instruction,
            contentBlockIds: chapterForm.referenceChapterIds,
            useCnReference: chapterForm.useCnReference,
            useEnReference: chapterForm.useEnReference,
        });
    }, [
        editor,
        chapter,
        rewritable,
        chapterForm,
        chapterTitle,
        clearPreview,
        startChapterRewrite,
        scrollToChapter,
        rewriteTask,
    ]);

    const handleDiscard = useCallback(() => {
        clearPreview();
        rewriteTask.reset();
    }, [clearPreview, rewriteTask]);

    // ========== 应用改稿 ==========

    const handleApply = useCallback(async () => {
        if (!editor) return;
        const blocks = await rewriteTask.confirm();
        if (!blocks) return;

        const target = targetRef.current;
        const headingInfo = target ? findHeadingNodeByBlockId(editor, target.blockId) : null;
        if (!headingInfo) {
            message.error('无法找到章节标题，改稿未应用');
            return;
        }
        try {
            const newContent = convertBlocksToTiptapContent(blocks);
            const range = findChapterRange(editor, {
                node: headingInfo.node,
                pos: headingInfo.pos,
                level: headingInfo.node.attrs.level,
            });
            const headingEnd = range.headingPos + range.headingNode.nodeSize;
            const { state, view } = editor;
            const { tr, schema } = state;
            const docBefore = state.doc;
            const nodes = (newContent.content || []).map((json) => schema.nodeFromJSON(json));
            const fragment = schema.nodes.doc.create(null, nodes).content;
            tr.replaceWith(headingEnd, range.to, fragment);
            // 内容已由后端 confirm 持久化，不需要再走自动保存
            tr.setMeta('skipDirtyTracking', true);
            // 整节替换会删掉旧公式等节点，各扩展的 filterTransaction 保护看到这个标记才放行
            tr.setMeta('allowChapterReplace', true);
            view.dispatch(tr);

            if (editor.state.doc === docBefore) {
                // 事务还是被某个保护插件拦下了：后端已经保存，重新拉一遍正文，保证页面和后端一致
                await reloadDocument();
                message.warning(`「${target?.title || '章节'}」的改稿已保存，正文已重新加载`);
            } else {
                message.success(`已应用「${target?.title || '章节'}」的改稿`);
                window.setTimeout(() => {
                    scrollEditorToPos(editor, headingInfo.pos, {
                        container: scrollContainerRef.current,
                        offset: 32,
                    });
                }, 80);
            }

            clearPreview();
            rewriteTask.reset();
            chapterForm.instruction = '';
            chapterForm.preset = null;
            chapterForm.referenceChapterIds = [];
        } catch (err) {
            console.error('替换章节内容失败:', err);
            message.error(err?.message || '替换章节内容失败');
        }
    }, [
        editor,
        rewriteTask,
        findHeadingNodeByBlockId,
        findChapterRange,
        scrollContainerRef,
        clearPreview,
        chapterForm,
        reloadDocument,
    ]);

    const handleCopy = useCallback(() => {
        if (previewEditor) copyToClipboard(previewEditor.getText());
    }, [previewEditor, copyToClipboard]);

    // ========== 渲染 ==========

    const renderTargetCard = () => {
        if (!chapter) {
            return (
                <div className={styles.targetCard}>
                    <div className={styles.targetCardLabel}>改稿对象</div>
                    <Select
                        className={styles.chapterSelect}
                        placeholder="选择要改的章节"
                        showSearch
                        optionFilterProp="label"
                        options={chapterOptions}
                        onChange={handlePickChapter}
                        value={undefined}
                        disabled={isBusy || hasResult}
                        popupMatchSelectWidth={false}
                        notFoundContent="没有可改稿的章节"
                    />
                    <div className={styles.targetHint}>
                        也可以点击左侧大纲，或正文标题旁的 <Sparkles size={12} /> 按钮选择章节
                    </div>
                </div>
            );
        }

        return (
            <div className={`${styles.targetCard} ${styles.targetCardActive}`}>
                <div className={styles.targetCardTop}>
                    <div className={styles.targetCardLabel}>改稿对象</div>
                    <Tooltip title="定位到该章节">
                        <Button
                            type="text"
                            size="small"
                            icon={<AimOutlined />}
                            onClick={handleLocate}
                        />
                    </Tooltip>
                </div>
                <div className={styles.targetTitle}>
                    <span className={styles.targetLevel}>{LEVEL_LABEL[chapter.level] || '节'}</span>
                    <span className={styles.targetTitleText} title={chapterTitle}>
                        {chapterTitle}
                    </span>
                </div>
                {isProtected && (
                    <Alert
                        type="warning"
                        showIcon
                        className={styles.inlineAlert}
                        message="参考文献由系统根据引用自动维护，不能改稿"
                    />
                )}
                {!isProtected && !rewritable && (
                    <>
                        <Alert
                            type="info"
                            showIcon
                            className={styles.inlineAlert}
                            message={
                                childChapters.length > 0
                                    ? '该章包含下级小节，请选择具体小节进行改稿'
                                    : '该章节暂不支持改稿，请选择其他小节'
                            }
                        />
                        <Select
                            className={styles.chapterSelect}
                            placeholder="选择要改的小节"
                            showSearch
                            optionFilterProp="label"
                            options={chapterOptions}
                            onChange={handlePickChapter}
                            value={undefined}
                            popupMatchSelectWidth={false}
                        />
                    </>
                )}
            </div>
        );
    };

    const renderForm = () => (
        <>
            <div className={styles.sectionLabel}>修改要求</div>
            <ChipGroup
                options={REVISE_PRESETS.map((p) => ({ value: p.key, label: p.label }))}
                value={chapterForm.preset}
                onChange={(key) => {
                    chapterForm.preset = chapterForm.preset === key ? null : key;
                }}
                disabled={isBusy}
            />
            {chapterForm.preset && (
                <div className={styles.presetDesc}>
                    {REVISE_PRESETS.find((p) => p.key === chapterForm.preset)?.text}
                </div>
            )}
            <TextArea
                value={chapterForm.instruction}
                onChange={(e) => {
                    chapterForm.instruction = e.target.value;
                }}
                placeholder={
                    chapterForm.preset
                        ? '补充具体要求（可选），例如：重点围绕 XX 展开、引用更多数据…'
                        : '描述你希望怎么改，例如：补充研究现状的最新进展，并用更学术的语言表达…'
                }
                autoSize={{ minRows: 3, maxRows: 7 }}
                maxLength={500}
                showCount
                disabled={isBusy}
                className={styles.instructionInput}
            />

            <Collapse
                ghost
                size="small"
                className={styles.advancedCollapse}
                items={[
                    {
                        key: 'advanced',
                        label: '高级选项',
                        children: (
                            <div className={styles.advancedBody}>
                                <div className={styles.switchRow}>
                                    <div className={styles.switchRowLeft}>
                                        <BookOpen size={14} />
                                        <span>参考中文文献</span>
                                    </div>
                                    <Switch
                                        size="small"
                                        checked={chapterForm.useCnReference}
                                        onChange={(checked) => {
                                            chapterForm.useCnReference = checked;
                                        }}
                                        disabled={isBusy}
                                    />
                                </div>
                                <div className={styles.switchRow}>
                                    <div className={styles.switchRowLeft}>
                                        <BookOpen size={14} />
                                        <span>参考英文文献</span>
                                    </div>
                                    <Switch
                                        size="small"
                                        checked={chapterForm.useEnReference}
                                        onChange={(checked) => {
                                            chapterForm.useEnReference = checked;
                                        }}
                                        disabled={isBusy}
                                    />
                                </div>
                                <div className={styles.fieldLabel}>参考其他章节内容（可选）</div>
                                <TreeSelect
                                    className={styles.chapterTreeSelect}
                                    value={chapterForm.referenceChapterIds}
                                    onChange={(value) => {
                                        chapterForm.referenceChapterIds = value || [];
                                    }}
                                    treeData={chapterTreeData}
                                    placeholder="选择要参考的小节"
                                    allowClear
                                    showSearch
                                    treeNodeFilterProp="title"
                                    multiple
                                    treeDefaultExpandAll
                                    maxTagCount="responsive"
                                    disabled={isBusy}
                                />
                            </div>
                        ),
                    },
                ]}
            />

            {phase === REWRITE_PHASE.FAILED && (
                <Alert
                    type="error"
                    showIcon
                    className={styles.inlineAlert}
                    message={error || '改稿失败，请重试'}
                    action={
                        <Button size="small" onClick={() => rewriteTask.reset()}>
                            知道了
                        </Button>
                    }
                />
            )}

            <Button
                type="primary"
                block
                size="large"
                icon={<Sparkles size={16} />}
                onClick={handleStart}
                loading={isBusy}
                disabled={!rewritable || isBusy}
                className={styles.primaryAction}
            >
                {isBusy ? 'AI 改稿中…' : '开始改稿'}
            </Button>
        </>
    );

    const renderProgress = () => (
        <div className={styles.progressCard}>
            <div className={styles.progressHeader}>
                <span className={styles.progressTitle}>
                    <Sparkles size={14} />
                    AI 正在改稿
                </span>
                <span className={styles.progressPercent}>{progress}%</span>
            </div>
            <Progress
                percent={progress}
                showInfo={false}
                strokeColor="var(--ant-color-primary, #a277ee)"
                size="small"
            />
            <div className={styles.progressStatus}>{statusText}</div>
            <div className={styles.progressFooter}>
                <span>改稿期间该章节暂时锁定，其他章节可继续编辑</span>
                <Button
                    size="small"
                    danger
                    icon={<StopOutlined />}
                    onClick={() => rewriteTask.cancel()}
                >
                    中断
                </Button>
            </div>
        </div>
    );

    const renderPreview = () => {
        const charCount =
            previewView === 'original'
                ? countDocChars(chapterState.previewOriginalContent)
                : countDocChars(chapterState.previewChapterContent);
        return (
            <ResultCard
                title="改稿结果"
                meta={`${previewView === 'original' ? '原文' : '修改后'} ${charCount} 字`}
                extra={
                    <Segmented
                        size="small"
                        value={previewView}
                        onChange={setPreviewView}
                        options={[
                            { label: '修改后', value: 'new' },
                            { label: '原文', value: 'original' },
                        ]}
                    />
                }
                actions={
                    <>
                        {error && (
                            <Alert
                                type="error"
                                showIcon
                                className={styles.inlineAlert}
                                message={error}
                            />
                        )}
                        <PreviewActions
                            onCancel={handleDiscard}
                            onCopy={handleCopy}
                            onRegenerate={handleStart}
                            onApply={handleApply}
                            applying={phase === REWRITE_PHASE.CONFIRMING}
                            applyText="应用到正文"
                        />
                    </>
                }
            >
                <div
                    className={`${styles.previewDoc} ${styles.previewDocNew}`}
                    style={{ display: previewView === 'new' ? 'block' : 'none' }}
                >
                    <EditorContent editor={previewEditor} />
                </div>
                <div
                    className={`${styles.previewDoc} ${styles.previewDocOriginal}`}
                    style={{ display: previewView === 'original' ? 'block' : 'none' }}
                >
                    <EditorContent editor={originalEditor} />
                </div>
            </ResultCard>
        );
    };

    return (
        <div className={styles.panelSection}>
            {renderTargetCard()}
            {hasResult && chapterState.previewChapterContent
                ? renderPreview()
                : isBusy
                  ? renderProgress()
                  : renderForm()}
        </div>
    );
};

export default ChapterRewritePanel;
